import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AGENCY_LINK_ROLE_LABELS,
  allowedAgencyLinkRolesFor,
  defaultAgencyLinkRoleFor,
  normalizeRegistrationNumber,
  validateBusinessNumberChecksum,
  withDirectionParticle,
  type AgencyLinkLookupCompany,
  type AgencyLinkLookupResponse,
  type AgencyLinkOutcome,
  type AgencyLinkRole,
} from '@safetyhub/shared';
import { Badge, Button, Card, FORM_CONTROL_CLASS, FORM_LABEL_CLASS, buttonClassName } from '@safetyhub/ui';
import { useAuthStore } from '../../stores/auth';
import { useToastStore } from '../../stores/toast';
import { apiErrorMessage } from '../../lib/api';
import { agencySiteLinkApi, agencySiteLinkQueryKeys } from '../../lib/agencySiteLink';
import {
  LINK_MODE_NOTICE,
  agencyLinkRequestPath,
  canConnectExistingSite,
  placementGroupOptions,
  siteLinkBlockReason,
} from '../../lib/agencySiteLinkView';

/** 새 현장 초안 입력 — 계약 `AgencySiteDraft`의 이름·주소만(나머지는 연결 뒤 현장 수정에서) */
interface DraftInput {
  name: string;
  address: string;
  addressDetail: string;
}

const EMPTY_DRAFT: DraftInput = { name: '', address: '', addressDetail: '' };

/**
 * 기관: 대행 현장 연결 화면(P4 · 결정 O2·O3 · O2 보강 · Q9 · Q12).
 *
 * 흐름: 사업자등록번호 → 조회(업체별 현장 이름·시군구·이미 붙은 대행 역할) → 역할·배치 그룹 → 기존 현장 연결 ∥ 새 현장 초안.
 * 결과는 갈래에 따라 「연결됨」(`CONNECTED`) 또는 「업체 승인 대기」(`REQUESTED`)다 — 둘 다 이 화면의 완료 상태로 끝난다.
 * ⚠️ 버튼을 막는 판정(같은 역할 이미 연결 · `NEW_SITE_ONLY`의 기존 현장)은 서버도 같은 이유로 거부한다 — 화면은 미리 보여 줄 뿐이다.
 */
export default function AgencyLinkConnectPage() {
  const membership = useAuthStore((s) => s.currentMembership);
  const organizationId = membership?.companyId;
  const category = membership?.company?.category ?? null;
  const { showToast } = useToastStore();
  const queryClient = useQueryClient();

  const roleOptions = useMemo(() => allowedAgencyLinkRolesFor(category), [category]);
  const [role, setRole] = useState<AgencyLinkRole>(() => defaultAgencyLinkRoleFor(category));

  const groupOptions = useMemo(
    () =>
      placementGroupOptions({
        isOwner: membership?.isOwner === true,
        groups: membership?.company?.groups ?? [],
        memberGroupIds: (membership?.groupMembers ?? []).map((g) => g.groupId),
      }),
    [membership],
  );
  const [groupId, setGroupId] = useState<string>('');
  const effectiveGroupId =
    groupId || groupOptions.find((g) => g.isDefault)?.id || groupOptions[0]?.id || '';

  const [regInput, setRegInput] = useState('');
  const [regError, setRegError] = useState<string | null>(null);
  const [lookup, setLookup] = useState<AgencyLinkLookupResponse | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);

  /** 새 현장 초안을 여는 업체 */
  const [draftCompanyId, setDraftCompanyId] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftInput>(EMPTY_DRAFT);

  const [outcome, setOutcome] = useState<AgencyLinkOutcome | null>(null);

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organizationId) return;
    const normalized = normalizeRegistrationNumber(regInput);
    if (normalized.length !== 10 || !validateBusinessNumberChecksum(normalized)) {
      setRegError('올바른 사업자등록번호 10자리를 입력해주세요.');
      return;
    }
    setRegError(null);
    setIsLookingUp(true);
    setDraftCompanyId(null);
    try {
      const result = await queryClient.fetchQuery({
        queryKey: agencySiteLinkQueryKeys.lookup(organizationId, normalized),
        queryFn: () => agencySiteLinkApi.lookup(organizationId, normalized),
        staleTime: 0,
      });
      setLookup(result);
    } catch (err) {
      setLookup(null);
      showToast(apiErrorMessage(err) ?? '업체를 조회하지 못했습니다.', 'error');
    } finally {
      setIsLookingUp(false);
    }
  };

  const onLinked = (result: AgencyLinkOutcome) => {
    setOutcome(result);
    void queryClient.invalidateQueries({ queryKey: ['sites'] });
    void queryClient.invalidateQueries({ queryKey: agencySiteLinkQueryKeys.all });
  };

  const connectMutation = useMutation({
    mutationFn: (siteId: string) =>
      agencySiteLinkApi.connect(organizationId!, {
        siteId,
        role,
        groupId: effectiveGroupId || undefined,
      }),
    onSuccess: onLinked,
    onError: (err) => showToast(apiErrorMessage(err) ?? '현장을 연결하지 못했습니다.', 'error'),
  });

  const createMutation = useMutation({
    mutationFn: (targetCompanyId: string) =>
      agencySiteLinkApi.createSite(organizationId!, {
        targetCompanyId,
        role,
        groupId: effectiveGroupId || undefined,
        site: {
          name: draft.name.trim(),
          address: draft.address.trim() || undefined,
          addressDetail: draft.addressDetail.trim() || undefined,
        },
      }),
    onSuccess: onLinked,
    onError: (err) => showToast(apiErrorMessage(err) ?? '새 현장을 만들지 못했습니다.', 'error'),
  });

  const isBusy = connectMutation.isPending || createMutation.isPending;

  if (outcome) {
    return <ConnectOutcomeView outcome={outcome} onAgain={() => { setOutcome(null); setLookup(null); setRegInput(''); setDraft(EMPTY_DRAFT); }} />;
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <Link to="/sites" className="text-sm text-ink-600 hover:text-ink-900">
          ← 현장 목록으로
        </Link>
        <h1 className="mt-2 text-[26px] font-extrabold tracking-tight text-ink-900">대행 현장 연결</h1>
        <p className="mt-1 text-[15px] font-medium text-ink-500">
          업체의 사업자등록번호로 현장을 찾아 대행 기관으로 연결합니다.
        </p>
      </div>

      <Card padding="xl" radius="lg" elevation="md">
        <form onSubmit={handleLookup} className="space-y-3">
          <label htmlFor="agency-link-reg" className={FORM_LABEL_CLASS}>
            업체 사업자등록번호
          </label>
          <div className="flex gap-2">
            <input
              id="agency-link-reg"
              className={FORM_CONTROL_CLASS}
              value={regInput}
              onChange={(e) => setRegInput(e.target.value)}
              placeholder="000-00-00000"
              inputMode="numeric"
              disabled={isLookingUp}
            />
            <Button type="submit" variant="primary" disabled={isLookingUp || !regInput.trim()}>
              {isLookingUp ? '조회 중...' : '조회'}
            </Button>
          </div>
          {regError && <p className="text-sm text-rose-600">{regError}</p>}
          {/* Q9 — 번호 없는 업체 행은 이 조회에 구조적으로 잡히지 않는다 */}
          <p className="text-xs text-ink-500">
            사업자등록번호가 등록되지 않은 업체는 조회되지 않습니다. 조회 기록은 감사 로그에 남습니다.
          </p>
        </form>
      </Card>

      <Card padding="xl" radius="lg" elevation="md">
        <h2 className="mb-4 text-lg font-medium text-ink-900">연결 설정</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="agency-link-role" className={FORM_LABEL_CLASS}>
              대행 역할
            </label>
            <select
              id="agency-link-role"
              className={FORM_CONTROL_CLASS}
              value={role}
              onChange={(e) => setRole(e.target.value as AgencyLinkRole)}
              disabled={isBusy}
            >
              {roleOptions.map((r) => (
                <option key={r} value={r}>
                  {AGENCY_LINK_ROLE_LABELS[r]}
                  {r === defaultAgencyLinkRoleFor(category) ? ' (기본)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="agency-link-group" className={FORM_LABEL_CLASS}>
              배치 그룹
            </label>
            {groupOptions.length > 1 ? (
              <select
                id="agency-link-group"
                className={FORM_CONTROL_CLASS}
                value={effectiveGroupId}
                onChange={(e) => setGroupId(e.target.value)}
                disabled={isBusy}
              >
                {groupOptions.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.isDefault ? `${g.name} (기본)` : g.name}
                  </option>
                ))}
              </select>
            ) : (
              <p id="agency-link-group" className="mt-2 text-sm text-ink-900">
                {groupOptions[0]?.name ?? '기본 그룹'}
              </p>
            )}
            <p className="mt-1 text-xs text-ink-500">연결한 현장은 우리 조직의 이 그룹에 배치됩니다.</p>
          </div>
        </div>
      </Card>

      {lookup && (
        <section aria-label="조회 결과" className="space-y-4">
          {lookup.companies.length === 0 ? (
            <Card padding="xl" radius="lg" elevation="md">
              <p className="text-sm text-ink-700">
                이 번호로 등록된 업체가 없습니다. 현장 추가 화면에서 수검업체 정보를 입력해 새 현장을 만들 수 있습니다.
              </p>
            </Card>
          ) : (
            lookup.companies.map((company) => (
              <CompanyResult
                key={company.companyId}
                company={company}
                role={role}
                busy={isBusy}
                onConnect={(siteId) => connectMutation.mutate(siteId)}
                draftOpen={draftCompanyId === company.companyId}
                onOpenDraft={() => {
                  setDraftCompanyId(company.companyId);
                  setDraft(EMPTY_DRAFT);
                }}
                onCloseDraft={() => setDraftCompanyId(null)}
                draft={draft}
                onDraftChange={setDraft}
                onCreate={() => createMutation.mutate(company.companyId)}
              />
            ))
          )}
        </section>
      )}
    </div>
  );
}

function CompanyResult({
  company,
  role,
  busy,
  onConnect,
  draftOpen,
  onOpenDraft,
  onCloseDraft,
  draft,
  onDraftChange,
  onCreate,
}: {
  company: AgencyLinkLookupCompany;
  role: AgencyLinkRole;
  busy: boolean;
  onConnect: (siteId: string) => void;
  draftOpen: boolean;
  onOpenDraft: () => void;
  onCloseDraft: () => void;
  draft: DraftInput;
  onDraftChange: (d: DraftInput) => void;
  onCreate: () => void;
}) {
  const notice = LINK_MODE_NOTICE[company.linkMode];
  const existingAllowed = canConnectExistingSite(company.linkMode);
  return (
    <Card padding="xl" radius="lg" elevation="md" data-testid={`agency-link-company-${company.companyId}`}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-bold text-ink-900">{company.name}</h3>
        <Badge tone={company.linkMode === 'DIRECT' ? 'emerald' : 'amber'} size="sm">
          {notice.badge}
        </Badge>
      </div>
      <p className="mt-1 text-sm text-ink-600" data-testid="agency-link-mode-notice">
        {notice.description}
      </p>

      {company.sites.length === 0 ? (
        <p className="mt-4 text-sm text-ink-500">이 업체가 소유한 현장이 없습니다.</p>
      ) : (
        <ul className="mt-4 divide-y divide-line">
          {company.sites.map((site) => {
            const block = siteLinkBlockReason(site, role);
            const disabledReason = !existingAllowed ? '기존 현장 연결은 기관 인증이 필요합니다.' : block;
            return (
              <li key={site.siteId} className="flex flex-wrap items-center justify-between gap-2 py-3">
                <div>
                  <p className="text-sm font-medium text-ink-900">{site.name}</p>
                  <p className="text-xs text-ink-500">{site.district ?? '주소 미등록'}</p>
                  {site.linkedAgencyRoles.length > 0 && (
                    <p className="mt-1 text-xs text-ink-500">
                      연결된 대행: {site.linkedAgencyRoles.map((r) => AGENCY_LINK_ROLE_LABELS[r]).join(' · ')}
                    </p>
                  )}
                  {disabledReason && <p className="mt-1 text-xs text-amber-700">{disabledReason}</p>}
                </div>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={busy || !!disabledReason}
                  onClick={() => onConnect(site.siteId)}
                >
                  {company.linkMode === 'REQUEST' ? '연결 요청' : '연결'}
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-4 border-t border-line pt-4">
        {draftOpen ? (
          <div className="space-y-3">
            <h4 className="text-sm font-bold text-ink-900">{company.name} 소유로 새 현장 만들기</h4>
            <div>
              <label htmlFor={`draft-name-${company.companyId}`} className={FORM_LABEL_CLASS}>
                현장 이름
              </label>
              <input
                id={`draft-name-${company.companyId}`}
                className={FORM_CONTROL_CLASS}
                value={draft.name}
                onChange={(e) => onDraftChange({ ...draft, name: e.target.value })}
                disabled={busy}
              />
            </div>
            <div>
              <label htmlFor={`draft-address-${company.companyId}`} className={FORM_LABEL_CLASS}>
                주소
              </label>
              <input
                id={`draft-address-${company.companyId}`}
                className={FORM_CONTROL_CLASS}
                value={draft.address}
                onChange={(e) => onDraftChange({ ...draft, address: e.target.value })}
                disabled={busy}
              />
            </div>
            <div>
              <label htmlFor={`draft-address-detail-${company.companyId}`} className={FORM_LABEL_CLASS}>
                상세 주소
              </label>
              <input
                id={`draft-address-detail-${company.companyId}`}
                className={FORM_CONTROL_CLASS}
                value={draft.addressDetail}
                onChange={(e) => onDraftChange({ ...draft, addressDetail: e.target.value })}
                disabled={busy}
              />
            </div>
            {company.linkMode === 'REQUEST' && (
              <p className="text-xs text-ink-500">업체 소유자가 승인하는 순간 이 내용으로 현장이 만들어집니다.</p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={onCloseDraft} disabled={busy}>
                취소
              </Button>
              <Button variant="primary" onClick={onCreate} disabled={busy || draft.name.trim().length < 2}>
                {company.linkMode === 'REQUEST' ? '새 현장 요청' : '새 현장 만들고 연결'}
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="secondary" size="sm" onClick={onOpenDraft} disabled={busy}>
            새 현장 만들기
          </Button>
        )}
      </div>
    </Card>
  );
}

function ConnectOutcomeView({ outcome, onAgain }: { outcome: AgencyLinkOutcome; onAgain: () => void }) {
  if (outcome.outcome === 'CONNECTED') {
    return (
      <div className="mx-auto max-w-xl">
        <Card padding="xl" radius="lg" elevation="md">
          <h1 className="text-xl font-bold text-ink-900">
            {outcome.siteCreated ? '새 현장을 만들고 연결했습니다' : '현장을 연결했습니다'}
          </h1>
          <p className="mt-2 text-sm text-ink-600">
            {withDirectionParticle(AGENCY_LINK_ROLE_LABELS[outcome.role])} 연결되었습니다. 업체에는 연결 사실이 알림으로 전달됩니다.
          </p>
          <div className="mt-6 flex gap-2">
            <Link to={`/sites/${outcome.siteId}`} className={buttonClassName('primary')}>
              현장으로 이동
            </Link>
            <Button variant="secondary" onClick={onAgain}>
              다른 현장 연결
            </Button>
          </div>
        </Card>
      </div>
    );
  }
  return (
    <div className="mx-auto max-w-xl">
      <Card padding="xl" radius="lg" elevation="md">
        <h1 className="text-xl font-bold text-ink-900">업체 승인을 기다립니다</h1>
        <p className="mt-2 text-sm text-ink-600">
          업체 소유자에게 {AGENCY_LINK_ROLE_LABELS[outcome.role]} 연결 요청을 보냈습니다. 승인되면 알림으로 알려드립니다.
        </p>
        <div className="mt-6 flex gap-2">
          <Link to={agencyLinkRequestPath(outcome.requestId)} className={buttonClassName('primary')}>
            요청 보기
          </Link>
          <Button variant="secondary" onClick={onAgain}>
            다른 현장 연결
          </Button>
        </div>
      </Card>
    </div>
  );
}
